import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';
import { YearLink } from 'site-modules/core-page/components/other-years/year-link/year-link';

import './years-links-list.scss';

const COLLAPSE_ID = 'other-review-years-collapse';
const YEAR_LIST_STYLE = 'years-list-grid list-unstyled m-0';
const COLLAPSE_BTN_GRID_SIZE = 0.7;
const YEARS_PER_LINE = { true: 4, false: 7 };

export function YearLinksList({
  years = [],
  currentYear = null,
  makeName = null,
  modelName = null,
  className = null,
  heading = null,
  headingClassName = 'heading-5 mb-0_75',
  renderEmptyHidden = false,
  isMobile = false,
  hasSpeculationRule = false,
  linkTrackingId = 'see_other_years_core',
  collapseTrackingId = 'see_other_years',
  ariaLabelSuffix = 'model years',
  creativeId = 'years-module',
}) {
  const [isOpen, toggleOpen] = useToggle(false);
  const containerRef = useRef();

  if (!renderEmptyHidden && years.length < 2) {
    return null;
  }

  const yearsPerLine = YEARS_PER_LINE[isMobile];
  const firstLinks = years.slice(0, yearsPerLine);
  const collapsedLinks = years.slice(yearsPerLine);
  const hasCollapse = !!collapsedLinks?.length;

  const collapseHeaderColumns = {
    '--mobile-columns': `${firstLinks.length}fr ${YEARS_PER_LINE.true - firstLinks.length + COLLAPSE_BTN_GRID_SIZE}fr`,
    '--desktop-columns': `${firstLinks.length}fr ${
      YEARS_PER_LINE.false - firstLinks.length + COLLAPSE_BTN_GRID_SIZE
    }fr`,
  };

  return (
    <div
      className={classnames('years-links-list', className, { 'd-none': renderEmptyHidden && years.length < 2 })}
      data-tracking-parent={creativeId}
      ref={containerRef}
    >
      {!!heading && <div className={headingClassName}>{heading}</div>}
      <div className="medium">
        <div
          className="collapse-header"
          style={{
            ...collapseHeaderColumns,
            display: 'grid',
            gridTemplateColumns: isMobile
              ? collapseHeaderColumns['--mobile-columns']
              : collapseHeaderColumns['--desktop-columns'],
          }}
        >
          <ul
            className="years-list-header list-unstyled"
            style={{
              margin: '-5px',
              padding: '5px',
              display: 'grid',
              gridTemplateColumns: `repeat(${firstLinks.length}, 1fr)`,
            }}
          >
            {firstLinks.map(({ name, url, noFollow }) => (
              <YearLink
                key={`year-${name}`}
                year={name}
                url={url}
                currentYear={currentYear}
                skipCurrentYear
                trackingId={linkTrackingId}
                noFollow={noFollow}
              />
            ))}
          </ul>
          {hasCollapse && (
            <div className={classnames('d-flex', { 'justify-content-end': firstLinks.length === yearsPerLine })}>
              <Button
                size="sm"
                color="link"
                onClick={toggleOpen}
                className="collapse-year-btn small text-transform-none px-0 py-0_5 border-0"
                style={{ fontSize: '.75rem', textTransform: 'none', minWidth: '3rem', fontWeight: 'normal' }}
                data-tracking-id={collapseTrackingId}
                aria-label={`${isOpen ? 'Less' : 'More'} ${makeName} ${modelName} ${ariaLabelSuffix}`}
                aria-controls={COLLAPSE_ID}
                aria-expanded={isOpen}
              >
                {isOpen ? 'less' : 'more'}{' '}
                <i
                  className={classnames('icon size-9', { 'icon-arrow-down3': !isOpen, 'icon-arrow-up3': isOpen })}
                  aria-hidden
                />
              </Button>
            </div>
          )}
        </div>
        {hasCollapse && (
          <AnimatedCollapse isOpen={isOpen} className="overflow-hidden-focusable" id={COLLAPSE_ID}>
            <div className="review-years wrapper-collapse-years">
              <ul className={classnames(YEAR_LIST_STYLE, 'mt-1_5')}>
                {collapsedLinks.map(({ name, url, noFollow }) => (
                  <YearLink
                    key={`year-${name}`}
                    year={name}
                    url={url}
                    currentYear={currentYear}
                    skipCurrentYear
                    trackingId={linkTrackingId}
                    noFollow={noFollow}
                  />
                ))}
              </ul>
            </div>
          </AnimatedCollapse>
        )}
      </div>

      {hasSpeculationRule && (
        <Fragment>
          <SpeculationRule
            observeRef={containerRef}
            selector=".years-lite-pills .years-list-header a"
            notSelector="[rel~=nofollow]"
          />
          <SpeculationRule
            observeRef={containerRef}
            action="prefetch"
            eagerness="moderate"
            selector=".years-lite-pills .years-list-header a[rel~=nofollow], .years-lite-pills .wrapper-collapse-years.review-years a"
          />
        </Fragment>
      )}
    </div>
  );
}

YearLinksList.propTypes = {
  years: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      noFollow: PropTypes.bool,
    })
  ),
  currentYear: PropTypes.string,
  makeName: PropTypes.string,
  modelName: PropTypes.string,
  className: PropTypes.string,
  heading: PropTypes.string,
  headingClassName: PropTypes.string,
  renderEmptyHidden: PropTypes.bool,
  isMobile: PropTypes.bool,
  hasSpeculationRule: PropTypes.bool,
  linkTrackingId: PropTypes.string,
  collapseTrackingId: PropTypes.string,
  ariaLabelSuffix: PropTypes.string,
  creativeId: PropTypes.string,
};
